import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import {
  fetchCompetitors,
  createCompetitor,
  fetchCompetitorById,
  deleteCompetitor as deleteCompetitorService,
} from "@/services/frontendServices/competitorsServices";
import { CompetitorDto } from "@/types";
import { toast } from "sonner";

// Keys for queries, to maintain consistency
export const competitorKeys = {
  all: ["competitors"] as const,
  lists: () => [...competitorKeys.all, "list"] as const,
  list: (filters: never) => [...competitorKeys.lists(), { filters }] as const,
  details: () => [...competitorKeys.all, "detail"] as const,
  detail: (competitorId: string) =>
    [...competitorKeys.details(), competitorId] as const,
};

// Hook to get all competitors
export const useCompetitors = (
  options?: Omit<
    UseQueryOptions<CompetitorDto[], Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<CompetitorDto[], Error>({
    queryKey: competitorKeys.lists(),
    queryFn: () => fetchCompetitors(),
    ...options,
  });
};

// Hook to get a specific competitor
export const useCompetitor = (competitorId: string) => {
  return useQuery({
    queryKey: competitorKeys.detail(competitorId),
    queryFn: () => fetchCompetitorById(competitorId),
    enabled: !!competitorId, // It only runs if we have an ID
  });
};

// Hook to create new competitor
export const useCreateCompetitor = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CompetitorDto,
    Error,
    { name: string; website?: string | null }
  >({
    mutationFn: async (data) => {
      const newCompetitor = await createCompetitor(data);
      return newCompetitor;
    },
    onSuccess: () => {
      // Invalidates the competitor list query to force a refetch
      queryClient.invalidateQueries({ queryKey: competitorKeys.lists() });
    },
    onError: (error) => {
      let errorMessage = "An unexpected error occurred";
      try {
        const errorDetails = JSON.parse(error.message);
        if (errorDetails.error && errorDetails.details) {
          errorMessage = `${errorDetails.error}: ${errorDetails.details}`;
        } else {
          errorMessage = errorDetails.error || "Failed to create competitor";
        }
      } catch (parseError) {
        logger.error("Failed to parse backend errors", parseError, {
          context: { source: "service", module: "useCompetitors" },
        });
      }

      toast.error("Error", {
        description: `Creating competitor: ${errorMessage}`,
        duration: 5000,
      });
    },
  });
};

// Hook to delete competitor
export const useDeleteCompetitor = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (competitorId: string) => {
      await deleteCompetitorService(competitorId);
    },
    onSuccess: () => {
      // Invalidates the competitor list query to force a refetch
      queryClient.invalidateQueries({ queryKey: competitorKeys.lists() });
    },
    onError: (error) => {
      let errorMessage = "An unexpected error occurred";
      try {
        const errorDetails = JSON.parse(error.message);
        if (errorDetails.error && errorDetails.details) {
          errorMessage = `${errorDetails.error}: ${errorDetails.details}`;
        } else {
          errorMessage = errorDetails.error || "Failed to delete competitor";
        }
      } catch (parseError) {
        logger.error("Failed to parse backend errors", parseError, {
          context: { source: "service", module: "useCompetitors" },
        });
      }

      toast.error("Error", {
        description: `Deleting competitor: ${errorMessage}`,
        duration: 5000,
      });
    },
  });
};
